/**
 * 客戶服務 API Service
 */

import apiClient from '../axios.js';

function cleanQueryParams(params = {}) {
  const cleaned = {};

  for (const [key, value] of Object.entries(params || {})) {
    if (value === '' || value === null || value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    cleaned[key] = value;
  }

  // 後端 schema: enabled 必須為 boolean；避免傳空字串或未知字串導致 400
  if (typeof cleaned.enabled === 'string') {
    if (cleaned.enabled === 'true') cleaned.enabled = true;
    else if (cleaned.enabled === 'false') cleaned.enabled = false;
    else delete cleaned.enabled;
  }

  return cleaned;
}

export const supportService = {
  // ==================== 客服工單 ====================

  /**
   * 查詢客服工單列表
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} 工單列表
   */
  async listTickets(params) {
    return apiClient.get('/support/tickets', {
      params: cleanQueryParams(params)
    });
  },

  /**
   * 查詢工單詳情
   * @param {string} ticketId - 工單 ID
   * @returns {Promise<Object>} 工單詳情
   */
  async getTicket(ticketId) {
    return apiClient.get(`/support/tickets/${ticketId}`);
  },

  /**
   * 建立客服工單
   * @param {Object} data - 工單資料
   * @returns {Promise<Object>} 建立結果
   */
  async createTicket(data) {
    return apiClient.post('/support/tickets', data);
  },

  /**
   * 更新工單
   * @param {string} ticketId - 工單 ID
   * @param {Object} data - 更新資料
   * @returns {Promise<Object>} 更新結果
   */
  async updateTicket(ticketId, data) {
    return apiClient.put(`/support/tickets/${ticketId}`, data);
  },

  /**
   * 更新工單狀態
   * @param {string} ticketId - 工單 ID
   * @param {string} status - 新狀態
   * @param {string} [remarks] - 備註
   * @returns {Promise<Object>} 更新結果
   */
  async updateTicketStatus(ticketId, status, remarks) {
    const body = { status };
    if (remarks) body.remarks = remarks;
    return apiClient.patch(`/support/tickets/${ticketId}/status`, body);
  },

  /**
   * 查詢工單處理歷程
   * @param {string} ticketId - 工單 ID
   * @returns {Promise<Object>} 歷程列表
   */
  async listTicketHistories(ticketId) {
    return apiClient.get(`/support/tickets/${ticketId}/histories`);
  },

  /**
   * 新增工單處理歷程
   * @param {string} ticketId - 工單 ID
   * @param {Object} data - { content, isInternal? }
   * @returns {Promise<Object>} 建立結果
   */
  async addTicketHistory(ticketId, data) {
    return apiClient.post(`/support/tickets/${ticketId}/histories`, data);
  },

  // ==================== 工單分類 ====================

  async listTicketCategories(params = {}) {
    return apiClient.get('/support/ticket-categories', {
      params: cleanQueryParams(params)
    });
  },

  async createTicketCategory(data) {
    return apiClient.post('/support/ticket-categories', data);
  },

  async updateTicketCategory(categoryId, data) {
    return apiClient.put(`/support/ticket-categories/${categoryId}`, data);
  },

  async deleteTicketCategory(categoryId) {
    return apiClient.delete(`/support/ticket-categories/${categoryId}`);
  },

  // ==================== FAQ 管理 ====================

  /**
   * 查詢 FAQ 列表
   * @param {Object} params - 查詢參數
   * @returns {Promise<Object>} FAQ 列表
   */
  async listFaqs(params) {
    const response = await apiClient.get('/support/faq', {
      params: cleanQueryParams(params)
    });
    return response;
  },

  /**
   * 查詢 FAQ 詳情
   * @param {string} id - FAQ ID
   * @returns {Promise<Object>} FAQ 詳情
   */
  async getFaq(id) {
    const response = await apiClient.get(`/support/faq/${id}`);
    return response;
  },

  /**
   * 建立 FAQ
   * @param {Object} data - FAQ 資料
   * @returns {Promise<Object>} 創建的 FAQ
   */
  async createFaq(data) {
    const response = await apiClient.post('/support/faq', data);
    return response;
  },

  /**
   * 更新 FAQ
   * @param {string} id - FAQ ID
   * @param {Object} data - FAQ 資料
   * @returns {Promise<Object>} 更新後的 FAQ
   */
  async updateFaq(id, data) {
    const response = await apiClient.put(`/support/faq/${id}`, data);
    return response;
  },

  /**
   * 刪除 FAQ
   * @param {string} id - FAQ ID
   * @returns {Promise<Object>} 刪除結果
   */
  async deleteFaq(id) {
    const response = await apiClient.delete(`/support/faq/${id}`);
    return response;
  }
};
